import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { billingApi } from '../services/billingApi';
import type { BillerSummary, CreateBillerRequest } from '../types/billing';

const BillersToolbar: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
    <Button variant="contained" onClick={onCreate}>New biller</Button>
    <GridToolbarQuickFilter placeholder="Search billers…" debounceMs={200} />
  </Box>
);

const Billers: React.FC = () => {
  const navigate = useNavigate();
  const [billers, setBillers] = useState<BillerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<CreateBillerRequest>({ email: '', firstName: '', lastName: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    billingApi.listBillers()
      .then(setBillers)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await billingApi.createBiller(form);
      setDialogOpen(false);
      setForm({ email: '', firstName: '', lastName: '' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const columns: GridColDef<BillerSummary>[] = [
    {
      field: 'name',
      headerName: 'Biller',
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
    {
      field: 'activePracticeCount',
      headerName: 'Practices',
      width: 110,
      type: 'number',
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'archived',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<BillerSummary>) =>
        params.row.archived
          ? <Chip label="Archived" size="small" />
          : <Chip label="Active" size="small" color="success" />,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Billers</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Internal RevMac billers and the practices they're assigned to. Click a row to manage grants.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid<BillerSummary>
            rows={billers}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/billers/${params.row.id}`)}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{ toolbar: () => <BillersToolbar onCreate={() => setDialogOpen(true)} /> }}
            sx={{
              border: 0,
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 600 },
            }}
          />
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New biller</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="First name" value={form.firstName} required
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            />
            <TextField
              label="Last name" value={form.lastName} required
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />
            <TextField
              label="Email" type="email" value={form.email} required
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={submitting || !form.email || !form.firstName || !form.lastName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billers;
